import { useMemo, useCallback, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from 'hooks'
import { selectFeatureFlags } from 'store/authSlice'

import { ComplianceAlertSeverity, FeatureType, RBACAction, RBACModule } from '@numeo/types-core'

import { useAuth } from 'hooks/auth'
import { usePermissions } from 'hooks/usePermissions'
import { useSidebarMenuSelection } from 'hooks/useSidebarMenuSelection'
import { useOnboardingConfig } from 'hooks/useOnboardingConfig'
import { useUserRole } from 'hooks/useUserRole'

import { isFeatureEnabled } from 'utils/feature-checker'
import { track, trackUnifiedEvent } from 'services/analytics'
import { AnalyticsEvent, EventCategory } from 'services/analytics/events'

import { useBoxTruckAccess } from 'features/box-truck-loads/hooks/useBoxTruckAccess'
import { useBoxTruckProfilesMode } from 'features/box-truck-loads/hooks/useBoxTruckProfilesMode'
import { useFleetData } from 'features/fleet/hooks/useFleetData'
import { useSafetyData } from 'features/safety/hooks/useSafetyData'
import { CompanySwitcher } from 'features/organization'
import ColorSchemeToggle from 'components/ColorSchemeToggle'

import { InvitationNotification } from './InvitationNotification'
import { useConnectDispatcherEmail } from './components/DispatcherAgentSetupModal/useConnectDispatcherEmail'
import { QrInstallModal } from 'features/load-radar/components/QrInstallModal'
import { useDownloadCenter } from 'features/download-center'

import { SidebarItem } from './sidebar/SidebarItem'
import { SidebarGroup } from './sidebar/SidebarGroup'
import { SidebarSectionHeader } from './sidebar/SidebarSectionHeader'
import { ProfileFooterItem } from './sidebar/ProfileFooterItem'
import { SidebarQuotaWidget } from './sidebar/SidebarQuotaWidget'
import { SidebarNotificationButton } from './sidebar/SidebarNotificationButton'
import { SidebarSearchButton } from './sidebar/SidebarSearchButton'
import { Envelope, LayoutSideContentLeft, LayoutSideContentRight } from './sidebar/icons'
import { Badge } from 'components/ds2/Badge'
import { BILLING_ITEM, HELP_CENTER_ITEM, SETTINGS_ITEM, SidebarBadgeTone, resolveSidebarMenu, type MenuGroup, type MenuItem } from './sidebar/menuConfig'
import { selectFleetComplianceAlerts } from 'store/fleetSlice'
import { selectSafetyComplianceAlerts } from 'store/safetySlice'
import { Gift } from 'lucide-react'
import { ReferralModal } from 'features/billing/referrals/ReferralModal'
import { isBillingFeatureEnabled } from 'features/billing/featureFlags'

interface SidebarProps {
    isOpen: boolean
    onToggle: () => void
}

/** TMS-surface marker for accounts without Fleet/Accounting/Safety features (set by TmsWelcomePage, survives logout). */
const readTmsAuthFlag = (): boolean => {
    try {
        return localStorage.getItem('numeo:tmsAuthUser') === 'true'
    } catch {
        // localStorage might be blocked; ignore.
        return false
    }
}

/**
 * Feature-aware sidebar.
 *
 * Layout is driven by the NUMBER of distinct feature families the user has
 * access to (`featureAny`-gated groups, see `menuConfig.tsx`), not directly by
 * the user's role:
 *
 *   - **0–1 families visible** → flat list with section headers. A single-scope
 *     user (dispatcher with just Extension, updater with just Updater) sees
 *     every visible group as a label + bare rows. Single-child groups collapse
 *     further to a top-level bare row.
 *   - **2+ families visible** → collapsible dropdowns with chevrons. Users that
 *     juggle multiple categories (e.g. updater with Extension + Updater, or a
 *     TMC with Accounting + Fleet + Safety + …) get a compact column where
 *     each category tucks behind a chevron. Single-child groups still
 *     short-circuit to a bare row so the UI never wraps a dropdown around a
 *     lone item.
 *
 * Visual styling uses the numeo-ds-v2 semantic tokens (`bg-surface`,
 * `bg-surface-secondary`, `border-border`, `bg-accent-soft`, …) — dark mode
 * comes for free since these self-flip, no `dark:` pairs needed.
 */

/**
 * Section headers grouping several `MENU_GROUPS` entries under one umbrella
 * label (reference: `new-agent-numeo-main/src/App.tsx` "NUMEO SPOT" /
 * "NUMEO ONE TMS" / "ORGANIZATION"). Groups in the same section must sit
 * contiguously in `MENU_GROUPS` — see the comment there — so the header
 * renders once, above the first visible group in that run.
 */
const SECTION_FOR_GROUP: Record<string, string> = {
    'find-loads': 'Numeo Spot',
    'box-truck-loads': 'Numeo Spot',
    'market-intel-group': 'Numeo Spot',
    dashboard: 'Numeo One TMS',
    'manage-loads': 'Numeo One TMS',
    accounting: 'Numeo One TMS',
    'instant-pay-group': 'Numeo One TMS',
    fleet: 'Numeo One TMS',
    safety: 'Numeo One TMS',
    'fuel-ai': 'Numeo One TMS',
    profiles: 'Organization',
}

export default function Sidebar({ isOpen, onToggle }: SidebarProps) {
    const { user, logout, application } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const { hasPermission } = usePermissions()
    // Not `usePermissions().visibleMenuIds` directly: a Dispatch Manager is not a role, so their selection
    // comes from somewhere else. One hook decides which of the two applies — see `useSidebarMenuSelection`.
    const visibleMenuIds = useSidebarMenuSelection()
    const boxTruckAccess = useBoxTruckAccess()
    const boxTruckProfilesMode = useBoxTruckProfilesMode()
    const flipFlags = useAppSelector(selectFeatureFlags)
    const { role } = useUserRole()
    const { isSpotEnabled } = useOnboardingConfig()
    // Hidden on Numeo Spot/dispatcher/Help Center routes — same gate `MasterLayout`'s old fixed
    // search bar used before search moved into the sidebar. Also keeps the SidebarSearchButton's
    // ⌘K listener unregistered on these routes (it isn't mounted here at all), instead of
    // globally intercepting ⌘K everywhere the sidebar renders, i.e. every authenticated route.
    const showGlobalSearch =
        isFeatureEnabled(application, FeatureType.GlobalSearch) &&
        !location.pathname.startsWith('/spot') &&
        !location.pathname.startsWith('/dispatcher') &&
        !location.pathname.startsWith('/help-center')

    const [qrModalOpen, setQrModalOpen] = useState(false)
    const [referralModalOpen, setReferralModalOpen] = useState(false)
    // Which `SECTION_FOR_GROUP` buckets (Numeo Spot / Numeo One TMS / Organization) are
    // collapsed in the grouped layout — toggled from the hover chevron on their header.
    // Only meaningful when `isOpen`; the collapsed-rail divider has no collapse control.
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
    const toggleSection = useCallback((section: string) => {
        setCollapsedSections((prev) => {
            const next = new Set(prev)
            if (next.has(section)) next.delete(section)
            else next.add(section)
            return next
        })
    }, [])
    // Derived fresh from `user` on every render — not cached in its own state — so the warning
    // badge can never linger stale after mount. It previously mirrored `user?.nylasEmails?.length`
    // into a `useState` synced by a `useEffect`, which meant the very first render (before that
    // effect had a chance to run) could show a stale/incorrect value — e.g. the badge missing on
    // first paint until some later re-render corrected it. `justConnected` is a one-way optimistic
    // flag so clicking "Enable" still flips the row instantly, without waiting on `user.nylasEmails`
    // to catch up via its own async refresh.
    const [justConnected, setJustConnected] = useState(false)
    const emailEnabled = justConnected || Boolean(user?.nylasEmails?.length)
    const { connectEmail } = useConnectDispatcherEmail({ onComplete: () => setJustConnected(true) })
    const { setIsOpen: openDownloadCenter } = useDownloadCenter()

    const { fetchComplianceAlerts: fetchFleetAlerts } = useFleetData()
    const { fetchComplianceAlerts: fetchSafetyAlerts } = useSafetyData()
    const fleetAlerts = useAppSelector(selectFleetComplianceAlerts).data
    const safetyAlerts = useAppSelector(selectSafetyComplianceAlerts).data
    // Only show Dispatch Help once confirmed on the operator allowlist (null/false → hide).
    const isDispatchHelpOperator = useAppSelector((state) => state.dispatchHelp.isOperator) === true
    // Only show Box Truck Loads once confirmed on its operator allowlist (null/false → hide).
    const isBoxTruckLoadsOperator = useAppSelector((state) => state.boxTruckLoads.isOperator) === true

    useEffect(() => {
        fetchFleetAlerts()
        fetchSafetyAlerts()
    }, [fetchFleetAlerts, fetchSafetyAlerts])

    const complianceBadge = useMemo(() => {
        const count = fleetAlerts.length
        if (count === 0) return undefined
        const hasExpired = fleetAlerts.some((a) => a.severity === ComplianceAlertSeverity.EXPIRED)
        return { label: String(count), tone: hasExpired ? SidebarBadgeTone.DANGER : SidebarBadgeTone.WARNING }
    }, [fleetAlerts])

    const driverComplianceBadge = useMemo(() => {
        const count = safetyAlerts.length
        if (count === 0) return undefined
        const hasExpired = safetyAlerts.some((a) => a.severity === ComplianceAlertSeverity.EXPIRED)
        return { label: String(count), tone: hasExpired ? SidebarBadgeTone.DANGER : SidebarBadgeTone.WARNING }
    }, [safetyAlerts])

    // localStorage isn't reactive: on a fresh signup, TmsWelcomePage sets numeo:tmsAuthUser in a
    // mount effect AFTER this sidebar has already rendered, so a one-time read would stay stale for
    // the whole session and hide the TMS "Home" entry. Hold the flag in state and refresh it when
    // TmsWelcomePage announces the change.
    const [isTmsAuthUser, setIsTmsAuthUser] = useState(readTmsAuthFlag)
    useEffect(() => {
        const refresh = () => setIsTmsAuthUser(readTmsAuthFlag())
        window.addEventListener('numeo:tmsAuthUserChanged', refresh)
        return () => window.removeEventListener('numeo:tmsAuthUserChanged', refresh)
    }, [])

    /**
     * Feature / RBAC checks for `filterMenuGroups`.
     *
     * When the account has **no major feature enabled at all** (Extension,
     * Updater/UpdaterLite, Accounting, Fleet, Safety) we treat `Extension`
     * as on so the user still sees Find Loads instead of an empty rail with
     * just Onboarding. This also covers the loading race where `application`
     * is briefly `null` after auth — without the fallback, the sidebar
     * would flash to "only Onboarding" until the auth call resolves and
     * then snap back to the real menu.
     */
    const flags = useMemo(() => {
        const hasAnyMajorFeature =
            isFeatureEnabled(application, FeatureType.Extension) ||
            isFeatureEnabled(application, FeatureType.Updater) ||
            isFeatureEnabled(application, FeatureType.UpdaterLite) ||
            isFeatureEnabled(application, FeatureType.Accounting) ||
            isFeatureEnabled(application, FeatureType.Fleet) ||
            isFeatureEnabled(application, FeatureType.Safety)

        return {
            hasFeature: (feature: FeatureType) => {
                if (!hasAnyMajorFeature && feature === FeatureType.Extension) return true
                // MarketIntel is gated by Flipt flag (featureFlags.marketIntel), not MongoDB
                if (feature === FeatureType.MarketIntel) return flipFlags.marketIntel ?? false
                return isFeatureEnabled(application, feature)
            },
            hasRbac: (module: RBACModule, action: RBACAction) => hasPermission(module, action),
            isTmsAuthUser,
            operatorFlags: { dispatchHelp: isDispatchHelpOperator, boxTruck: isBoxTruckLoadsOperator },
            fliptFlags: { fuelTollWorkspace: flipFlags.fuelTollWorkspace === true, generalAgent: flipFlags.generalAgent === true },
            // Real account role + RBAC grants (not the layout role): splits the Box Truck section into
            // the dispatcher working surfaces vs the admin-only driver-management consoles.
            boxTruckAccess,
            boxTruckProfilesMode,
            visibleMenuIds,
        }
    }, [
        application,
        hasPermission,
        flipFlags,
        isTmsAuthUser,
        isDispatchHelpOperator,
        isBoxTruckLoadsOperator,
        boxTruckAccess,
        boxTruckProfilesMode,
        visibleMenuIds,
    ])

    // Both come from one call so they cannot disagree — and so the "a selection that leaves nothing is
    // ignored" rule can look at the grouped rows and the hand-drawn ones together.
    const { groups: menuGroups, allowsMenuId } = useMemo(() => resolveSidebarMenu(role, flags), [role, flags])

    /**
     * Dropdown layout trigger — count how many visible groups are gated by a
     * `featureAny` entry. Those are the "feature families" the user crosses over
     * between (Find Loads ↔ Extension, Manage Loads ↔ Updater/UpdaterLite,
     * Accounting ↔ Accounting, Fleet ↔ Fleet, Safety ↔ Safety). When the user
     * spans 2+ families the sidebar earns its collapsible chevrons; with just
     * one family the label-only flat view is cleaner.
     *
     * Examples:
     *   - Dispatcher with Extension only      → 1 family  → flat
     *   - Updater with Updater only           → 1 family  → flat
     *   - Updater with Updater + Extension    → 2 families → dropdown
     *   - TMC with all features               → 5 families → dropdown
     */
    const useDropdownLayout = useMemo(() => menuGroups.filter((g) => g.featureAny && g.featureAny.length > 0).length >= 2, [menuGroups])

    /**
     * All paths we care about for "active" highlighting — flattened menu
     * children plus the standalone footer items. Used below to disambiguate
     * overlapping prefixes: e.g. `/updater-agent` (Fleet View) is a proper
     * prefix of `/updater-agent/scheduler` (Dispatch Board), and a naive
     * `startsWith` check would light up both when the user navigates to
     * Dispatch Board. We resolve that by picking the LONGEST matching
     * registered path as the single active item.
     */
    const allPaths = useMemo(
        () => [...menuGroups.flatMap((g) => g.children.map((c) => c.path)), BILLING_ITEM.path, HELP_CENTER_ITEM.path, SETTINGS_ITEM.path],
        [menuGroups]
    )

    const isActive = useCallback(
        (path: string) => {
            // /dispatcher redirects to /spot when Spot is enabled — treat them as the same active path.
            if (path === '/dispatcher' && location.pathname === '/spot') return true
            // Exact-match wins unconditionally.
            if (location.pathname === path) return true
            // For a prefix match, require that no OTHER registered menu path is
            // strictly more specific for the current URL — otherwise Fleet View
            // (`/updater-agent`) would also highlight while on Dispatch Board
            // (`/updater-agent/scheduler`).
            if (!location.pathname.startsWith(`${path}/`)) return false
            return !allPaths.some(
                (p) => p !== path && p.length > path.length && p.startsWith(`${path}/`) && (location.pathname === p || location.pathname.startsWith(`${p}/`))
            )
        },
        [location.pathname, allPaths]
    )

    /** The real URL a menu path opens — the same one a click navigates to, so a new tab lands on the same page. */
    const resolveHref = useCallback(
        (path: string) => {
            // Special-case: when "Spot" is enabled, route the dispatcher dashboard there.
            if (path === '/dispatcher' && isSpotEnabled) return '/spot'
            if (path === '/dispatcher/load-hub') return '/dispatcher/load-hub?entry=sidebar'
            return path
        },
        [isSpotEnabled]
    )

    const handleNavigate = useCallback((path: string) => navigate(resolveHref(path)), [navigate, resolveHref])

    /**
     * "Enable" CTA pill rendered in the row's suffix slot when an item has
     * `paywall` set and the gating feature is OFF. Stops propagation so the
     * surrounding row's click handler (if any) doesn't also fire — the row
     * itself is rendered disabled in that state, but defending against
     * future refactors is cheap.
     */
    const enablePill = (url: string) => (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="rounded-md bg-accent-soft px-2 py-0.5 text-[11px] font-semibold text-accent-soft-foreground hover:bg-accent-soft-hover"
        >
            Enable
        </a>
    )

    // TMS module gating is NOT a sidebar lock: items stay normal/clickable, and a missing entitlement
    // surfaces as the full-page FeatureGate takeover on navigation (ADR-020) — not a greyed "Upgrade"
    // row. The only sidebar lock left is the FeatureType `paywall` ("Enable" CTA, e.g. email connect).

    /** True when the item is locked by a FeatureType paywall (the org doesn't have the feature). */
    const isPaywalled = (item: MenuItem) => !!item.paywall && !flags.hasFeature(item.paywall.feature)

    /** The locked-state suffix CTA: the "Enable" pill for FeatureType paywalls. */
    const lockSuffix = (item: MenuItem) => (isPaywalled(item) && item.paywall ? enablePill(item.paywall.enableUrl) : undefined)

    // `fleet-compliance` and `safety-driver-compliance` rows show a LIVE expired/expiring-document
    // count instead of their static config badge. Shared by `renderMenuItem` (flat layout, bare
    // top-level rows) and `renderGroup`'s `popoverItems` (dropdown layout's expanded children) —
    // both need to resolve the same live badge, not just the flat layout.
    const resolveBadge = (item: MenuItem) => {
        if (item.id === 'fleet-compliance' && complianceBadge) return complianceBadge
        if (item.id === 'safety-driver-compliance' && driverComplianceBadge) return driverComplianceBadge
        return item.badge
    }

    const renderMenuItem = (item: MenuItem) => {
        const paywalled = isPaywalled(item)
        const suffix = lockSuffix(item)
        const badge = resolveBadge(item)
        const icon = item.icon
        return (
            <SidebarItem
                key={item.id}
                icon={icon}
                label={item.label}
                active={isActive(item.path)}
                isOpen={isOpen}
                badge={paywalled ? undefined : badge}
                disabled={paywalled}
                suffix={suffix}
                sidebarItemId={item.id}
                href={resolveHref(item.path)}
                onClick={paywalled ? undefined : () => handleNavigate(item.path)}
            />
        )
    }

    const renderGroup = (group: MenuGroup) => {
        // Single visible child: skip the dropdown shell entirely and render the child as
        // a bare top-level row. A chevron over a one-row "menu" is just visual noise — it
        // forces an extra click for no organisational benefit. Matches the same shortcut
        // `renderFlatGroup` applies for single-item groups in dispatcher view.
        if (group.children.length === 1) {
            return (
                <div key={group.id} className="shrink-0">
                    {renderMenuItem(group.children[0])}
                </div>
            )
        }
        const childActive = group.children.some((child) => isActive(child.path))
        return (
            <SidebarGroup
                key={group.id}
                groupId={group.id}
                icon={group.icon}
                label={group.label}
                isOpen={isOpen}
                childActive={childActive}
                popoverItems={group.children.map((child) => {
                    const paywalled = isPaywalled(child)
                    return {
                        id: child.id,
                        label: child.label,
                        icon: child.icon,
                        section: child.section,
                        active: isActive(child.path),
                        // Hide the badge when paywalled — the Enable CTA in `suffix` becomes the
                        // row's only call to action. Otherwise resolve the same live compliance
                        // count `renderMenuItem` uses, not the static config badge.
                        badge: paywalled ? undefined : resolveBadge(child),
                        disabled: paywalled,
                        suffix: lockSuffix(child),
                        href: resolveHref(child.path),
                        onClick: paywalled ? () => undefined : () => handleNavigate(child.path),
                    }
                })}
            />
        )
    }

    /** Render a single group as a label + bare rows (no dropdown). */
    const renderFlatGroup = (group: MenuGroup) => {
        // Single-item groups (e.g. the dispatcher's "Home › Dashboard") render as a
        // bare row — a section header above one child is visual noise.
        if (group.children.length === 1) {
            return (
                <div key={group.id} className="shrink-0">
                    {renderMenuItem(group.children[0])}
                </div>
            )
        }
        return (
            <div key={group.id} className="flex shrink-0 flex-col gap-0.5">
                {isOpen && <SidebarSectionHeader label={group.label} />}
                {group.children.map((child) => renderMenuItem(child))}
            </div>
        )
    }

    // Billing / Settings live in the main nav flow under the "Organization" section,
    // alongside Profiles (reference design). "Get 1 Month Free" now sits separately,
    // directly above the Free Usage card at the bottom of the nav — see `renderReferralItem`.
    // These two sit outside MENU_GROUPS, so `filterMenuGroups` never sees them — a role's menu selection
    // has to be applied here as well, through the same predicate, or "show only the Loadboard" would still
    // leave Billing and Settings on the rail. Help Center is not filtered: it is `alwaysVisible`.
    const renderOrganizationExtras = () => {
        const rows: JSX.Element[] = []
        if (allowsMenuId(BILLING_ITEM.id))
            rows.push(
                <SidebarItem
                    key="billing"
                    icon={BILLING_ITEM.icon}
                    label="Billing"
                    isOpen={isOpen}
                    active={isActive(BILLING_ITEM.path)}
                    href={BILLING_ITEM.path}
                    // `src` nav state labels billing_page_viewed's source as 'sidebar' (vs limit_modal / deep_link).
                    onClick={() => navigate(BILLING_ITEM.path, { state: { src: 'sidebar' } })}
                />
            )
        if (allowsMenuId(SETTINGS_ITEM.id))
            rows.push(
                <SidebarItem
                    key="settings"
                    icon={SETTINGS_ITEM.icon}
                    label={SETTINGS_ITEM.label}
                    isOpen={isOpen}
                    active={isActive(SETTINGS_ITEM.path)}
                    href={resolveHref(SETTINGS_ITEM.path)}
                    onClick={() => handleNavigate(SETTINGS_ITEM.path)}
                />
            )
        return rows
    }

    /** "Get 1 Month Free" — rendered just above the Free Usage card, not inside Organization. */
    const renderReferralItem = () =>
        isBillingFeatureEnabled('referrals') && (
            <SidebarItem
                key="referral"
                icon={<Gift className="h-4 w-4" />}
                label="Get 1 Month Free"
                isOpen={isOpen}
                onClick={() => {
                    try {
                        trackUnifiedEvent({
                            category: EventCategory.FEATURE_USE,
                            action: AnalyticsEvent.REFERRAL_PROMO_CLICKED,
                            placement: 'sidebar',
                        })
                    } catch {
                        /* analytics must never block the referral modal */
                    }
                    setReferralModalOpen(true)
                }}
            />
        )

    /**
     * Flat layout used for the single-scope roles (`dispatcher`, `updater`).
     *
     * Every visible group renders as a section header + bare items — no dropdown
     * chevrons. Both roles operate inside a narrow feature set so tucking things
     * away behind collapsible headers would add friction without organisational
     * value. `renderFlatGroup` already shortcuts to a bare row when a group filters
     * down to a single visible child, so there's no extra case to handle here.
     * Billing/Settings trail every group as their own "Organization" section.
     */
    const renderFlatLayout = () => {
        // Resolved before the header, because a role's menu selection can hide both rows — and a header
        // introducing nothing is worse than no header. This is the layout a single-feature dispatcher
        // gets, which is exactly who the Box Truck Dispatcher preset is for.
        const organizationExtras = renderOrganizationExtras()
        return [
            ...menuGroups.map((group) => renderFlatGroup(group)),
            isOpen && organizationExtras.length > 0 && <SidebarSectionHeader key="organization" label="Organization" />,
            ...organizationExtras,
        ]
    }

    /**
     * Grouped layout used for `updater` and `tmc` roles.
     *
     * Multi-feature users see several distinct categories (Manage Loads / Accounting /
     * Fleet / Safety / etc.), so collapsible dropdowns are warranted. Groups still tagged
     * `flat: true` in menuConfig render as a label + items even here.
     *
     * `renderGroup` itself shortcuts to a bare top-level row when a group has only ONE
     * visible child after role/feature filtering — wrapping a single item in a dropdown
     * shell is needless friction.
     *
     * `MENU_GROUPS` is ordered so groups sharing a `SECTION_FOR_GROUP` label sit
     * contiguously — the section header (open) or divider (collapsed) is inserted
     * once, right before the first visible group in each run. Billing/Settings
     * trail Profiles inside the same "Organization" run.
     */
    const renderGroupedLayout = () => {
        let lastSection: string | undefined
        const rows = menuGroups.flatMap((group) => {
            const section = SECTION_FOR_GROUP[group.id]
            const isNewSection = section !== undefined && section !== lastSection
            if (section !== undefined) lastSection = section

            // Collapsing a section hides its groups but the header itself always stays — the
            // chevron there is the only way to expand it again.
            const sectionCollapsed = isOpen && section !== undefined && collapsedSections.has(section)
            const groupRows = sectionCollapsed ? [] : [group.flat ? renderFlatGroup(group) : renderGroup(group)]

            if (isNewSection) {
                groupRows.unshift(
                    isOpen ? (
                        <SidebarSectionHeader key={`section-${section}`} label={section} collapsed={sectionCollapsed} onToggle={() => toggleSection(section)} />
                    ) : (
                        <div key={`divider-${section}`} className="mx-2 my-1.5 h-px bg-border" />
                    )
                )
            }
            return groupRows
        })

        // Billing/Settings/referral must always be reachable, regardless of whether `profiles`
        // itself survived role/feature filtering. `profilesGroup` requires one of
        // Updater/UpdaterLite/Fleet/Accounting/Safety/FuelAI — an account with e.g. ONLY
        // InstantPay has no `profiles` group at all, so appending these extras from inside the
        // `profiles` iteration (as before) silently dropped Billing/Settings from the sidebar
        // entirely for that account. Appended here instead, after every group, so they show up
        // whether or not `profiles` was present — `profiles` is always the last MENU_GROUPS
        // entry, so this lands in the same visual spot when it IS present.
        const organizationCollapsed = isOpen && collapsedSections.has('Organization')
        // Header stays OUTSIDE the collapse guard — same reasoning as the group loop above ("the
        // header itself always stays — the chevron there is the only way to expand it again").
        // A prior version guarded this too, which made the synthesized header (rendered only when
        // `profiles` was absent) disappear the moment the section was collapsed, with no control
        // left to expand it back — `collapsedSections` is plain `useState`, so only a reload
        // recovered Billing/Settings for that account shape.
        // Resolved BEFORE the header, because a role's menu selection can now take both of these away —
        // and a header introducing nothing is worse than no header. When `lastSection` is already
        // 'Organization' the profiles group has emitted one, so there is content either way.
        const organizationExtras = renderOrganizationExtras()
        if (lastSection !== 'Organization' && organizationExtras.length > 0) {
            rows.push(
                isOpen ? (
                    <SidebarSectionHeader
                        key="section-Organization"
                        label="Organization"
                        collapsed={organizationCollapsed}
                        onToggle={() => toggleSection('Organization')}
                    />
                ) : (
                    <div key="divider-Organization" className="mx-2 my-1.5 h-px bg-border" />
                )
            )
        }
        if (!organizationCollapsed) {
            rows.push(...organizationExtras)
        }

        return rows
    }

    return (
        <>
            <aside
                className={[
                    // `bg-surface-secondary` for the sidebar column — deliberately DIFFERENT from the
                    // main content surface (`bg-surface`) so the sidebar reads as a separate column
                    // against the main area.
                    'sticky top-0 z-30 flex h-dvh shrink-0 flex-col gap-3 border-r border-border bg-surface-secondary pt-3',
                    // Narrow the transition to ONLY the properties that actually change on toggle.
                    // Using `transition-all` here caused visible zoom-in/out flicker when menu items
                    // were clicked rapidly — any micro-property change (hover, focus, scrollbar
                    // gutter, etc.) would animate for 300ms and compound across re-renders.
                    'transition-[width,padding] duration-300 ease-in-out',
                    'relative overflow-visible',
                    isOpen ? 'w-[var(--Sidebar-width)] p-3' : 'w-[60px] px-3 py-1',
                ].join(' ')}
                // Width matches the reference's 220px open / 56px collapsed (new-agent-numeo-main
                // App.tsx line 1021). MasterLayout.tsx mirrors this for its own layout CSS var.
                style={{ ['--Sidebar-width' as string]: '220px' }}
            >
                {/* ── Collapse / expand toggle (matches reference pill) ───── */}
                <button
                    type="button"
                    onClick={onToggle}
                    aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                    // Toggle pill sits on top of the sidebar column — painted in the main surface
                    // colour (`bg-surface`) so it visibly stands out from the `bg-surface-secondary`
                    // column behind it, matching the reference design.
                    className="absolute -right-3.5 top-16 z-40 flex h-7 w-7 items-center justify-center rounded-xl border border-border bg-surface text-foreground-muted shadow-surface transition hover:bg-default hover:text-foreground"
                >
                    {isOpen ? <LayoutSideContentLeft /> : <LayoutSideContentRight />}
                </button>
                {/* ── Header: logo + theme toggle ───────────────────────── */}
                {/*
                 * Keep `ColorSchemeToggle` rendered in both open and collapsed
                 * states. Previously it was gated behind `isOpen`, which meant
                 * a user who collapsed the rail had no way to swap themes from
                 * the sidebar anymore. In collapsed mode the header just shows
                 * the toggle pill centred at the top (no logo).
                 */}
                <div className={`flex items-center gap-2 ${isOpen ? 'justify-between' : 'justify-center'}`}>
                    {isOpen && (
                        <button type="button" onClick={() => navigate('/')} className="flex items-center" aria-label="Go to home">
                            {/*
                             * Inlined (rather than an <img> PNG swap) so the "numeo" wordmark's
                             * fill="currentColor" can inherit the theme's foreground color
                             * directly — no separate light/dark asset files to keep in sync.
                             * Figma spec: https://www.figma.com/design/QDoa5u83nNciI95Vx3s39s/Spot?node-id=8172-130138.
                             */}
                            <svg width="93" height="24" viewBox="0 0 93 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Numeo">
                                <g style={{ color: 'var(--numeo-accent)' }}>
                                    <path
                                        fill="currentColor"
                                        d="M11.989 6.42054C12.0353 6.44191 12.0815 6.46328 12.1292 6.48531C12.6866 6.74917 13.1599 7.09919 13.6046 7.52591C13.6426 7.5612 13.6804 7.5965 13.7196 7.63286C14.6073 8.482 15.1243 9.60398 15.3477 10.7995C15.3552 10.8384 15.3627 10.8773 15.3705 10.9173C15.3924 11.071 15.3959 11.221 15.3952 11.3758C15.3954 11.423 15.3954 11.423 15.3954 11.4711C15.3955 11.5761 15.3952 11.6811 15.395 11.786C15.395 11.8615 15.395 11.9369 15.395 12.0123C15.395 12.2168 15.3947 12.4213 15.3944 12.6257C15.3942 12.8395 15.3942 13.0533 15.3942 13.2671C15.394 13.6719 15.3936 14.0766 15.3932 14.4813C15.3927 14.9422 15.3924 15.4029 15.3923 15.8638C15.3919 16.8116 15.3911 17.7594 15.3902 18.7072C14.4642 18.7072 13.5383 18.7072 12.5842 18.7072C12.5838 18.4877 12.5834 18.2682 12.583 18.0421C12.5816 17.3158 12.5792 16.5896 12.5764 15.8633C12.5747 15.4231 12.5733 14.9829 12.5727 14.5427C12.5721 14.1587 12.571 13.7749 12.5691 13.3909C12.5682 13.1879 12.5675 12.9848 12.5675 12.7817C12.5676 12.5901 12.5668 12.3985 12.5654 12.2069C12.5651 12.137 12.565 12.0672 12.5652 11.9973C12.5679 11.1029 12.319 10.2934 11.6811 9.63883C11.0251 9.01529 10.2477 8.77578 9.36075 8.78987C8.63801 8.81672 7.9751 9.15185 7.46804 9.65727C6.94804 10.2177 6.62939 10.8901 6.62547 11.6621C6.62526 11.6923 6.62506 11.7226 6.62483 11.7538C6.62418 11.8546 6.62382 11.9555 6.62347 12.0563C6.62307 12.1288 6.62264 12.2013 6.62219 12.2738C6.62105 12.4703 6.62016 12.6668 6.61931 12.8632C6.61838 13.0687 6.61721 13.2742 6.61607 13.4797C6.61395 13.8685 6.61204 14.2574 6.61021 14.6463C6.60811 15.0892 6.60577 15.532 6.60342 15.9749C6.59858 16.8856 6.59406 17.7963 6.58968 18.7072C5.66372 18.7072 4.73776 18.7072 3.78374 18.7072C3.7799 17.7185 3.77676 16.7299 3.77493 15.7413C3.77407 15.2821 3.77287 14.823 3.77092 14.364C3.76922 13.9634 3.76813 13.5628 3.76774 13.1623C3.76753 12.9506 3.76701 12.7388 3.76576 12.5271C3.76459 12.3271 3.76426 12.127 3.76451 11.927C3.76446 11.8543 3.76413 11.7816 3.76348 11.7089C3.75754 11.0223 3.85171 10.3414 4.11322 9.70211C4.12781 9.66531 4.14239 9.62852 4.1574 9.59062C4.36706 9.07364 4.62472 8.6007 4.97414 8.16362C4.99233 8.14068 5.0105 8.11772 5.02923 8.09409C5.9879 6.89457 7.31082 6.19994 8.81205 5.92764C9.87467 5.8118 11.0271 5.9396 11.989 6.42054Z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M16.0704 1.87109C16.2443 1.92907 16.2832 2.04322 16.368 2.19527C16.6665 2.71064 17.0163 3.18542 17.4308 3.61418C17.4525 3.63703 17.474 3.6599 17.4963 3.68345C17.6732 3.86641 17.8594 4.0172 18.0686 4.16155C18.1151 4.19409 18.1151 4.19409 18.1624 4.22729C18.401 4.39244 18.6461 4.54447 18.8988 4.68697C18.9635 4.72401 19.0265 4.764 19.0889 4.80458C19.0889 4.83264 19.0889 4.8607 19.0889 4.88961C19.0027 4.94508 19.0027 4.94508 18.879 5.01184C17.689 5.6882 16.8782 6.55022 16.21 7.74039C16.1921 7.76768 16.174 7.79497 16.1554 7.82309C16.1274 7.82309 16.0994 7.82309 16.0704 7.82309C16.0259 7.76364 16.0259 7.76364 15.9778 7.67961C15.9504 7.63301 15.9504 7.63302 15.9225 7.58549C15.8932 7.53474 15.8932 7.53474 15.8631 7.48298C15.6995 7.2077 15.5277 6.94391 15.3344 6.68849C15.314 6.66155 15.2937 6.63462 15.2727 6.60687C15.0595 6.32893 14.849 6.07225 14.5691 5.85947C14.5439 5.84 14.5186 5.82054 14.4927 5.80049C14.2873 5.64343 14.0782 5.49144 13.8676 5.34132C13.8387 5.3206 13.8098 5.29987 13.7799 5.27851C13.5735 5.13327 13.3596 5.00832 13.1369 4.88961C13.1369 4.86155 13.1369 4.83349 13.1369 4.80458C13.1991 4.75807 13.1991 4.75807 13.2864 4.70742C13.3183 4.68857 13.3503 4.6697 13.3831 4.65028C13.4176 4.63016 13.452 4.61006 13.4876 4.58935C13.9783 4.2962 14.4255 3.97026 14.8375 3.57167C14.8704 3.53977 14.9035 3.50787 14.9375 3.47501C15.3929 3.00464 15.7267 2.42499 16.0704 1.87109Z"
                                    />
                                </g>
                                <path
                                    fill="currentColor"
                                    className="text-foreground"
                                    d="M29.3574 12.4755V18.6426H26.8769V8.11751H29.2478V9.90595H29.3711C29.6133 9.31665 29.9993 8.84842 30.5292 8.50124C31.0636 8.15406 31.7238 7.98047 32.5095 7.98047C33.2358 7.98047 33.8685 8.13578 34.4076 8.44643C34.9511 8.75706 35.3715 9.20702 35.6684 9.79632C35.9699 10.3856 36.1183 11.1005 36.1138 11.9411V18.6426H33.6332V12.3248C33.6332 11.6213 33.4505 11.0708 33.085 10.6734C32.7242 10.276 32.224 10.0773 31.5844 10.0773C31.1505 10.0773 30.7644 10.1732 30.4264 10.3651C30.0929 10.5523 29.8303 10.8242 29.6384 11.1805C29.4511 11.5368 29.3574 11.9685 29.3574 12.4755ZM45.7297 14.216V8.11751H48.2101V18.6426H45.805V16.7719H45.6954C45.4579 17.3612 45.0673 17.8432 44.5236 18.2177C43.9846 18.5924 43.3199 18.7797 42.5297 18.7797C41.8399 18.7797 41.2301 18.6266 40.7001 18.3205C40.1748 18.0099 39.7636 17.5599 39.4667 16.9706C39.1697 16.3768 39.0213 15.6595 39.0213 14.8191V8.11751H41.5018V14.4353C41.5018 15.1022 41.6846 15.6322 42.0499 16.025C42.4154 16.4179 42.895 16.6142 43.4889 16.6142C43.8544 16.6142 44.2085 16.5252 44.5511 16.3471C44.8938 16.1689 45.1746 15.904 45.394 15.5522C45.6177 15.1959 45.7297 14.7505 45.7297 14.216ZM51.1468 18.6426V8.11751H53.5178V9.90595H53.6411C53.8603 9.30296 54.2235 8.83243 54.7306 8.49439C55.2376 8.15177 55.8429 7.98047 56.5464 7.98047C57.259 7.98047 57.8598 8.15406 58.3486 8.50124C58.8419 8.84385 59.189 9.31208 59.3901 9.90595H59.4998C59.7327 9.32122 60.1256 8.85527 60.6784 8.5081C61.2357 8.15635 61.8957 7.98047 62.6586 7.98047C63.6271 7.98047 64.4174 8.28653 65.0294 8.89867C65.6417 9.5108 65.9478 10.4039 65.9478 11.5779V18.6426H63.4604V11.9616C63.4604 11.3084 63.2868 10.831 62.9396 10.5295C62.5924 10.2234 62.1676 10.0704 61.6651 10.0704C61.0666 10.0704 60.5984 10.2577 60.2604 10.6323C59.9269 11.0023 59.7601 11.4843 59.7601 12.0781V18.6426H57.3276V11.8588C57.3276 11.3152 57.1631 10.8813 56.8342 10.5569C56.5098 10.2326 56.085 10.0704 55.5597 10.0704C55.2033 10.0704 54.879 10.1618 54.5867 10.3445C54.2943 10.5227 54.0613 10.7762 53.8878 11.1051C53.7141 11.4294 53.6274 11.8086 53.6274 12.2426V18.6426H51.1468ZM73.5097 18.8481C72.4544 18.8481 71.543 18.6289 70.7757 18.1903C70.0128 17.7472 69.4257 17.1214 69.0146 16.3128C68.6034 15.4997 68.3979 14.5426 68.3979 13.4417C68.3979 12.3591 68.6034 11.4089 69.0146 10.5912C69.4303 9.7689 70.0104 9.12936 70.7551 8.67255C71.4997 8.21116 72.3745 7.98047 73.3795 7.98047C74.0281 7.98047 74.6403 8.08553 75.2159 8.29567C75.7961 8.50124 76.3077 8.82102 76.7508 9.25498C77.1985 9.68896 77.5502 10.2417 77.8061 10.9132C78.0618 11.5802 78.1897 12.3751 78.1897 13.2978V14.0585H69.5628V12.3865H75.812C75.8075 11.9114 75.7047 11.4888 75.5036 11.1188C75.3026 10.7442 75.0218 10.4496 74.6608 10.2349C74.3045 10.0202 73.8888 9.9128 73.4137 9.9128C72.9067 9.9128 72.4612 10.0361 72.0775 10.2828C71.6938 10.5249 71.3946 10.8447 71.1799 11.2421C70.9698 11.635 70.8624 12.0667 70.8579 12.5372V13.9967C70.8579 14.6089 70.9698 15.1342 71.1936 15.5728C71.4175 16.0067 71.7304 16.3402 72.1324 16.5732C72.5344 16.8016 73.0049 16.9158 73.5439 16.9158C73.9048 16.9158 74.2314 16.8656 74.5238 16.765C74.8161 16.6599 75.0697 16.5069 75.2844 16.306C75.4991 16.105 75.6612 15.856 75.7709 15.5591L78.0869 15.8195C77.9408 16.4316 77.6622 16.966 77.251 17.4228C76.8444 17.8751 76.3236 18.2269 75.6887 18.4781C75.0537 18.7248 74.3273 18.8481 73.5097 18.8481ZM85.2652 18.8481C84.2374 18.8481 83.3466 18.622 82.5928 18.1697C81.8391 17.7175 81.2544 17.0848 80.8386 16.2717C80.4275 15.4585 80.2219 14.5083 80.2219 13.4212C80.2219 12.3339 80.4275 11.3815 80.8386 10.5638C81.2544 9.74606 81.8391 9.11108 82.5928 8.65884C83.3466 8.20659 84.2374 7.98047 85.2652 7.98047C86.293 7.98047 87.1838 8.20659 87.9376 8.65884C88.6913 9.11108 89.2738 9.74606 89.6849 10.5638C90.1005 11.3815 90.3084 12.3339 90.3084 13.4212C90.3084 14.5083 90.1005 15.4585 89.6849 16.2717C89.2738 17.0848 88.6913 17.7175 87.9376 18.1697C87.1838 18.622 86.293 18.8481 85.2652 18.8481ZM85.2789 16.861C85.8362 16.861 86.3021 16.7079 86.6768 16.4019C87.0513 16.0912 87.3301 15.6756 87.5127 15.1548C87.7001 14.634 87.7937 14.0538 87.7937 13.4142C87.7937 12.7702 87.7001 12.1877 87.5127 11.667C87.3301 11.1416 87.0513 10.7236 86.6768 10.413C86.3021 10.1024 85.8362 9.94706 85.2789 9.94706C84.7079 9.94706 84.2327 10.1024 83.8536 10.413C83.4791 10.7236 83.1981 11.1416 83.0107 11.667C82.8281 12.1877 82.7366 12.7702 82.7366 13.4142C82.7366 14.0538 82.8281 14.634 83.0107 15.1548C83.1981 15.6756 83.4791 16.0912 83.8536 16.4019C84.2327 16.7079 84.7079 16.861 85.2789 16.861Z"
                                />
                            </svg>
                        </button>
                    )}
                    <ColorSchemeToggle />
                </div>

                {/* ── Company Switcher ──────────────────────────────────── */}
                <CompanySwitcher collapsed={!isOpen} />

                {/* ── Search — opens the global search overlay (⌘K). Sits outside <nav>,
                    same as the Company Switcher above, so it's always reachable without
                    scrolling instead of disappearing under the rest of the menu. ────── */}
                {showGlobalSearch && (
                    <div className="shrink-0">
                        <SidebarSearchButton isOpen={isOpen} />
                    </div>
                )}

                {/* ── Main navigation ───────────────────────────────────── */}
                {/*
                 * `scrollbar-hide` (index.css) keeps the nav scrollable — via wheel, trackpad,
                 * or touch — without ever rendering a visible scrollbar track/thumb, so group
                 * expand/collapse transitions never shift row widths by a gutter popping in
                 * and out (no gutter is ever reserved, since no bar ever renders).
                 */}
                {/* `pb-4` (16px) reserves the same visual gap the divider below used to supply
                    externally — but INSIDE nav's own scrollable box instead. That matters when
                    content overflows: the old external gap (aside's `gap-3` + the divider's own
                    margin) was dead space nav's `overflow-y-auto` couldn't scroll into, so the
                    LAST real row (e.g. the Free Usage card) got clipped a full 16px above the
                    divider with untouchable blank space beneath it. With the padding moved inside
                    nav, that same 16px is part of the scrollable content — you can scroll to it,
                    and the clip boundary in the worst case lands flush against the divider instead
                    of stopping short of it. */}
                <nav className="scrollbar-hide flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden pb-4">
                    {/*
                     * Layout decision is feature-driven, not role-driven:
                     *   - 2+ feature families visible (e.g. Extension + Updater, or a TMC with
                     *     Accounting + Fleet) → collapsible dropdowns.
                     *   - 0-1 feature families visible (single-scope dispatcher or updater, or
                     *     a minimal TMC setup) → label + bare items.
                     *   See `useDropdownLayout` above for how the family count is derived.
                     */}
                    {useDropdownLayout ? renderGroupedLayout() : renderFlatLayout()}

                    {/* Spacer to push the quota widget / footer down when there's room */}
                    <div className="flex-1" />

                    {isOpen && renderReferralItem()}
                    {isOpen && <SidebarQuotaWidget hasSpotAccess={flags.hasFeature(FeatureType.Extension)} />}
                </nav>

                {/* Full-bleed divider — the sidebar column has 12px of padding (`p-3` on
                    the <aside>), so a plain `border-t` on the footer div below stops short
                    of the edges. Negative-margin this one line out to the column's full
                    width instead of stretching the whole footer's content with it.
                    `-mt-3` cancels the <aside>'s own `gap-3` above this divider — nav's
                    `pb-4` now supplies that spacing itself (see the comment there for why).
                    `mb-1` keeps the untouched side: <aside>'s `gap-3` + this `mb-1` still
                    total the intended 16px between the divider and the footer below. */}
                <div className="-mx-3 -mt-3 mb-1 shrink-0 border-t border-border" />

                {/* ── Footer: utility row / profile ────── */}
                <div className="flex shrink-0 flex-col gap-3">
                    {/*
                     * Compact utility row — Email / Notifications / Downloads — replacing
                     * the old one-row-each footer stack (reference: new-agent-numeo-main's
                     * bottom Email/Notifications/Archive/Help icon row, minus Help — reachable
                     * from the profile popover below instead). Billing and Settings moved up
                     * into the main nav under the "Organization" section header instead.
                     * Notifications here takes over the idle "browse notifications" bell +
                     * dropdown that used to be a floating, draggable, route-gated widget
                     * (`components/NotificationCenter.tsx`) — see `SidebarNotificationButton`
                     * for why the urgent "special" toasts that widget also shows are
                     * deliberately left floating as-is. Horizontal when the sidebar is open,
                     * stacked when collapsed (matching how every other collapsed row in this
                     * sidebar is a full-width icon).
                     */}
                    <div className={isOpen ? 'flex shrink-0 items-center gap-0.5' : 'flex shrink-0 flex-col gap-0.5'}>
                        <SidebarNotificationButton isOpen={isOpen} />
                        <button
                            type="button"
                            // When email isn't connected yet, open the connect picker directly
                            // (same `connectEmail` flow the old sidebar "Enable" pill used) instead
                            // of just navigating to an empty inbox.
                            onClick={() => (emailEnabled ? handleNavigate('/emails?type=all') : connectEmail())}
                            title="Email"
                            className={[
                                'relative flex h-9 items-center justify-center rounded-xl transition-colors',
                                isOpen ? 'flex-1' : 'w-full',
                                isActive('/emails') ? 'bg-accent-soft text-foreground' : 'text-foreground hover:bg-default',
                            ].join(' ')}
                        >
                            <span className="relative inline-flex">
                                <Envelope size={18} />
                                {!emailEnabled && (
                                    <span className="absolute -right-2.5 -top-3.5 z-10">
                                        <Badge
                                            variant="primary"
                                            type="warning"
                                            size="sm"
                                            label="!"
                                            style={{ border: 'none', minWidth: 12, height: 12, padding: 0, fontSize: 8, justifyContent: 'center' }}
                                        />
                                    </span>
                                )}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => openDownloadCenter(true)}
                            title="Downloads"
                            className={[
                                'flex h-9 items-center justify-center rounded-xl text-foreground transition-colors hover:bg-default',
                                isOpen ? 'flex-1' : 'w-full',
                            ].join(' ')}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-[18px] w-[18px]">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                />
                            </svg>
                        </button>
                    </div>

                    <ProfileFooterItem
                        isOpen={isOpen}
                        name={user?.name ?? ''}
                        email={user?.email ?? ''}
                        onInstallApp={() => setQrModalOpen(true)}
                        onHelp={() => navigate(HELP_CENTER_ITEM.path)}
                        onLogout={() => {
                            track(AnalyticsEvent.LOGOUT_SUCCESS, { method: 'email/password' })
                            logout()
                        }}
                    />
                </div>

                <InvitationNotification isOpen={isOpen} />
            </aside>
            <QrInstallModal open={qrModalOpen} onClose={() => setQrModalOpen(false)} />
            <ReferralModal open={referralModalOpen} onClose={() => setReferralModalOpen(false)} />
        </>
    )
}
